/* oxlint-disable import/no-unassigned-import -- Prism grammar components register themselves on the global Prism */
import Prism, { type TokenStream } from 'prismjs'
// The grammars the editor loads (@lexical/code-prism FacadePrism.ts); each extends the global `Prism`.
import 'prismjs/components/prism-clike'
import 'prismjs/components/prism-diff'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-markup'
import 'prismjs/components/prism-markdown'
import 'prismjs/components/prism-c'
import 'prismjs/components/prism-css'
import 'prismjs/components/prism-go'
import 'prismjs/components/prism-objectivec'
import 'prismjs/components/prism-sql'
import 'prismjs/components/prism-powershell'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-rust'
import 'prismjs/components/prism-swift'
import 'prismjs/components/prism-typescript'
import 'prismjs/components/prism-java'
import 'prismjs/components/prism-cpp'
import { codeHighlightingLexicalTheme } from '../../infra/ui/code-highlighting/src/code-theme'
import type { CodeTokenTone } from '../../infra/ui/code-highlighting/src/token-tones'
import type { CodeRun } from './document'

const toneOfClass: Record<string, CodeTokenTone> = {
  'text-error': 'error',
  'text-warning': 'warning',
  'text-info': 'info',
  'text-success': 'success',
  'text-primary': 'primary',
  'text-on-surface-secondary': 'muted',
}

/**
 * Code runs as the editor highlights them (block-editor.tsx `registerCodeHighlighting(editor, reviewCodeTokenizer)`, whose
 * tokenizer is `Prism.tokenize` with no fallback language): Prism tokens flattened like @lexical/code-prism
 * `$mapTokensToLexicalStructure` (a string takes the type of the token holding it, a `prefix` its alias, `unchanged`
 * none), each type painted with its `codeHighlightingLexicalTheme` colour; comments are italic (`italic` in the theme).
 * A language Prism has no grammar for stays plain.
 */
export function editorCodeRuns(source: string, language: string): CodeRun[] {
  const grammar = Prism.languages[language]
  if (!grammar) return [{ text: source }]
  const out: CodeRun[] = []
  const walk = (tokens: TokenStream, type?: string) => {
    for (const token of Array.isArray(tokens) ? tokens : [tokens]) {
      if (typeof token === 'string') {
        out.push({ text: token, ...(type === undefined ? {} : _style(type)) })
        continue
      }
      const { content, alias } = token
      if (typeof content === 'string') walk(content, token.type === 'prefix' && typeof alias === 'string' ? alias : token.type)
      else if (Array.isArray(content)) walk(content, token.type === 'unchanged' ? undefined : token.type)
    }
  }
  walk(Prism.tokenize(source, grammar))
  return out
}

function _style(type: string): Pick<CodeRun, 'tone' | 'italic'> {
  const classes = ((codeHighlightingLexicalTheme as Record<string, string | undefined>)[type] ?? '').split(' ')
  const tone = toneOfClass[classes[0] ?? '']
  return { ...(tone ? { tone } : {}), ...(classes.includes('italic') ? { italic: true } : {}) }
}
